import React, { useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { LinkElem } from 'modules/Header/styles';
import PropTypes from 'prop-types';
import { PERMISSIONS, URLS, USER_ROLE_TYPES } from '_constants';
import { AuthEntity } from '_entities';
import { useDispatch, useSelector } from 'react-redux';
import { signOut } from '_entities/Auth/Auth.actions';
import { removeToken } from 'utils/services/token';
import { getUserRoleType } from '_entities/Auth/Auth.selectors';
import LogOutPopup from '../LogOutPopup';
import * as Styled from './styles';

const { getAuthenticatedUser } = AuthEntity.selectors;

function MenuItem({ subRoute, index, setIsOpenDropDown, tabRef, subHeaderFocus }) {
  const [isOpenPopup, setIsOpenPopup] = useState(false);
  const location = useLocation();
  const history = useHistory();
  const isAuthenticated = useSelector(getAuthenticatedUser);
  const userRoleType = useSelector(getUserRoleType);
  const dispatch = useDispatch();

  const handleLogInAsUser = useCallback(async () => {
    try {
      await dispatch(signOut());
    } catch (e) {
      // Non-fatal: token is still cleared and login flow continues in finally.
    } finally {
      removeToken();
      history.push(URLS.login, { as_user: true });
    }
  }, [dispatch, history]);

  const handleSelect = useCallback(
    (e, path, permission) => {
      if (permission === PERMISSIONS.LOGIN_AS_USER) {
        e.preventDefault();
        handleLogInAsUser();
        return;
      }
      if (path === URLS.logout) {
        e.preventDefault();
        setIsOpenPopup(true);
        return;
      }
      if (path === location.pathname) {
        return;
      }
      setIsOpenDropDown(false);
    },
    [location.pathname, setIsOpenDropDown, setIsOpenPopup, handleLogInAsUser],
  );

  const handleSelectByKeyboard = useCallback(
    (e, path, isHelp, permission) => {
      if (permission === PERMISSIONS.LOGIN_AS_USER && e.keyCode === 13) {
        handleLogInAsUser();
        return;
      }
      if ((path === location.pathname && e.keyCode !== 27) || e.keyCode === 9) {
        e.preventDefault();
        return;
      }
      if (path === URLS.logout && e.keyCode === 13) {
        e.preventDefault();
        setIsOpenPopup(() => true);
      }
      if ([27, 13].includes(e.keyCode)) {
        setIsOpenDropDown(false);
      }
      if ([32, 13].includes(e.keyCode) && path !== URLS.logout && !isHelp) history.push(path);
    },
    [history, location.pathname, setIsOpenDropDown, handleLogInAsUser],
  );

  // This is for short time and will be deleted when infographics will be done
  const subInfoRoute = isAuthenticated ? `${subRoute.path}?new_ui=true&is_auth=true` : `${subRoute.path}?new_ui=true`;

  if (subRoute.isDevider)
    return (
      <Styled.DropDownContentItem>
        <Styled.DeviderText>{subRoute.title}</Styled.DeviderText>
      </Styled.DropDownContentItem>
    );

  return (
    <>
      {subRoute.devider && (
        <Styled.DropDownContentItem>
          <Styled.DeviderText>{subRoute.devider}</Styled.DeviderText>
        </Styled.DropDownContentItem>
      )}
      <Styled.DropDownContentItem border={subRoute.border} borderAll={subRoute.borderAll}>
        {!subRoute.isInfo && subRoute.permission !== PERMISSIONS.LOGIN_AS_USER && (
          <LinkElem
            to={subRoute.isHelp ? { pathname: subRoute.href } : subRoute.path}
            onClick={(e) => handleSelect(e, subRoute.path, subRoute.permission)}
            onKeyDown={(e) => handleSelectByKeyboard(e, subRoute.path, subRoute.isHelp, subRoute.permission)}
            aria-label={subRoute.title}
            name={subRoute.path}
            ref={tabRef}
            target={subRoute.isHelp && '_blank'}
            tabIndex={subHeaderFocus === index ? 0 : -1}
          >
            {subRoute.title}
          </LinkElem>
        )}
        {userRoleType === USER_ROLE_TYPES.MULTI_ACCOUNT_MANAGER &&
          subRoute.permission === PERMISSIONS.LOGIN_AS_USER && (
            <LinkElem
              to={subRoute.isHelp ? { pathname: subRoute.href } : subRoute.path}
              onClick={(e) => handleSelect(e, subRoute.path, subRoute.permission)}
              onKeyDown={(e) => handleSelectByKeyboard(e, subRoute.path, subRoute.isHelp, subRoute.permission)}
              aria-label={subRoute.title}
              name={subRoute.path}
              ref={tabRef}
              target={subRoute.isHelp && '_blank'}
              tabIndex={subHeaderFocus === index ? 0 : -1}
            >
              {subRoute.title}
            </LinkElem>
          )}
        {/* This is for short time and will be deleted when infographics will be done */}
        {subRoute.isInfo && <a href={subInfoRoute}>{subRoute.title}</a>}
        {subRoute.path === URLS.logout && subRoute.permission !== PERMISSIONS.LOGIN_AS_USER && (
          <LogOutPopup isOpenPopup={isOpenPopup} setIsOpenPopup={setIsOpenPopup} />
        )}
      </Styled.DropDownContentItem>
    </>
  );
}

MenuItem.propTypes = {
  subRoute: PropTypes.shape({
    path: PropTypes.string,
    href: PropTypes.string,
    border: PropTypes.bool,
    title: PropTypes.string,
    devider: PropTypes.string,
    isInfo: PropTypes.bool,
    isHelp: PropTypes.bool,
    isDevider: PropTypes.bool,
    borderAll: PropTypes.bool,
    permission: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  }).isRequired,
  index: PropTypes.number.isRequired,
  tabRef: PropTypes.object.isRequired,
  setIsOpenDropDown: PropTypes.func.isRequired,
  subHeaderFocus: PropTypes.number.isRequired,
};

export default MenuItem;
